import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { ApiGroupFilter } from 'module/ui/components/molecules/field-filter/simple-search-filter/simple-search-filter';
import { HttpClient } from 'api/http-client';
import { BlacklistedDomain } from 'queries/emails';
import { PaginatedEmailsResponse, Email } from './types';

export type { Email, PaginatedEmailsResponse };

export type EmailProviderInfo = { gmail: string } | { outlook: string };

export enum EmailIntelligenceEnabledTo {
  SpecificTeamMembersOnly = 'SPECIFIC_TEAM_MEMBERS_ONLY',
  AllTeamMembers = 'ALL_TEAM_MEMBERS',
}

export interface EmailSettings {
  id?: string;
  emailIntelligenceEnabled: boolean;
  backSyncStartRange: string;
  backSyncStartDate: string;
  ftueCompleted: boolean;
  emailProviderInfo?: EmailProviderInfo;
  emailIntelligenceEnabledTo?: EmailIntelligenceEnabledTo;
}

export class EmailAPIClient extends HttpClient {
  async getBlacklistedDomains() {
    return this.get<BlacklistedDomain[]>(
      'scarlet/emails/settings/blacklisted-domains',
    );
  }

  async createBlacklistedDomain(email: string) {
    return this.post('scarlet/emails/settings/blacklisted-domains', { email });
  }

  async updateBlacklistedDomain({
    oldEmail,
    newEmail,
  }: {
    oldEmail: string;
    newEmail: string;
  }) {
    return this.put('scarlet/emails/settings/blacklisted-domains', {
      oldEmail,
      newEmail,
    });
  }

  async deleteBlacklistedDomain(email: string) {
    return this.delete('scarlet/emails/settings/blacklisted-domains', {
      data: { email },
    });
  }

  async getEmailSettings() {
    return this.get<EmailSettings>('scarlet/emails/settings');
  }
  async updateEmailSettings(settings: EmailSettings) {
    return this.put<EmailSettings, EmailSettings>(
      'scarlet/emails/settings',
      settings,
    );
  }

  async createEmailSettings(settings: EmailSettings) {
    return this.post<EmailSettings, EmailSettings>(
      'scarlet/emails/settings',
      settings,
    );
  }

  public async getAllEmailsWithFilters(
    filter: GroupedFilter | undefined,
    pagination?: {
      offset?: number;
      limit?: number;
    },
  ): Promise<PaginatedEmailsResponse> {
    var filterQueryString;

    if (filter) {
      filterQueryString = ApiGroupFilter.toQueryStringFormatForGroups(
        filter.groupCriteria,
      );
    }

    let url = '/scarlet/emails/search?';

    if (filter && filterQueryString) {
      url += `${filterQueryString}`;
      url += `&match=${filter.type}&`;
    }

    if (pagination?.offset !== undefined) {
      url += `offset=${pagination.offset}`;
    }
    if (pagination?.limit) {
      url += `&limit=${pagination.limit}`;
    }

    return this.get<PaginatedEmailsResponse>(url);
  }

  public async searchEmailsByCompanyId(
    companyId: number,
    options?: {
      offset?: number;
      limit?: number;
      searchTerm?: string;
    },
  ): Promise<PaginatedEmailsResponse> {
    const { offset = 0, limit = 25, searchTerm } = options || {};
    let url = `/email/search?email*companyId.oneOf=${companyId}&match=all&offset=${offset}&limit=${limit}`;

    if (searchTerm) {
      url += `&email*subject.contains=${encodeURIComponent(searchTerm)}`;
    }

    return this.get<PaginatedEmailsResponse>(url);
  }
}

export const EmailAPI = new EmailAPIClient();
